import { create } from 'zustand'

import { GridInitialState } from '@mui/x-data-grid-premium'

import { gridStatePost } from 'Common/api/SettingsApi'
import {
    migrateLegacyOldSaves,
    renameUnnamedRoyaltyView,
    upgradeBgaActiveFilterField,
    upgradeSeasonFilterShape,
} from 'Common/components/gridSearch/utils/legacySaveMigration'
import {
    SavedSearchType,
    SearchScope,
    SearchScopeSchema,
    columnScopeKey,
    supportsColumnViews,
} from 'Common/store/searchScopes'

export {
    FILTER_ONLY_SCOPES,
    SearchScopeSchema,
    columnScopeKey,
    supportsColumnViews,
} from 'Common/store/searchScopes'
export type { SavedSearchType, SearchScope } from 'Common/store/searchScopes'

export type SearchScopeType = (SavedSearchType | null) & {
    scope: SearchScope
}

export function extractColumnState(state: GridInitialState | undefined | null) {
    if (!state) return {}
    return {
        columns: {
            columnVisibilityModel: state.columns?.columnVisibilityModel,
            dimensions: state.columns?.dimensions,
            orderedFields: state.columns?.orderedFields,
        },
        pinnedColumns: state.pinnedColumns,
        density: state.density,
    } as GridInitialState
}

function hasColumnContent(state: GridInitialState): boolean {
    const cols = state.columns
    const hasCols =
        !!cols &&
        ((cols.columnVisibilityModel && Object.keys(cols.columnVisibilityModel).length > 0) ||
            (cols.dimensions && Object.keys(cols.dimensions).length > 0) ||
            (cols.orderedFields && cols.orderedFields.length > 0))
    const pinned = state.pinnedColumns
    const hasPinned =
        !!pinned && ((pinned.left?.length ?? 0) > 0 || (pinned.right?.length ?? 0) > 0)
    return !!(hasCols || hasPinned)
}

type Searches = Record<SearchScope, SearchScopeType | null>

type UserSettingsStore = {
    userSettings: Partial<Record<string, SavedSearchType[]>>
    activeSearches: Searches
    savedSearches: Searches
    activeColumnViews: Searches
    savedColumnViews: Searches

    setUserSettings: (
        settings: Partial<Record<string, SavedSearchType[]>>,
        options?: { hideAgeAndSubGroup?: boolean },
    ) => void
    getAvailableSearches: (scope: SearchScope | string) => SavedSearchType[]
    getAvailableColumnViews: (scope: SearchScope) => SavedSearchType[]
    setActiveSearch: (scope: SearchScope, search: SearchScopeType | null) => void
    setSavedSearch: (scope: SearchScope, search: SearchScopeType | null) => void
    setActiveColumnView: (scope: SearchScope, view: SearchScopeType | null) => void
    setSavedColumnView: (scope: SearchScope, view: SearchScopeType | null) => void
}

const emptyScopeRecord = (): Searches => ({
    searchStatesActivity: null,
    searchStatesAgreement: null,
    searchStatesAsset: null,
    searchStatesBundle: null,
    searchStatesEdition: null,
    searchStatesEditionSalesSheet: null,
    searchStatesWork: null,
    searchStatesWorkLaunchBrief: null,
    searchStatesConcept: null,
    searchStatesContact: null,
})

export function parseSearchList(raw: unknown): SavedSearchType[] {
    if (!raw) return []
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw)
        } catch {
            return []
        }
    }
    return raw as SavedSearchType[]
}

function migrateLegacyToColumnViews(settings: Partial<Record<string, SavedSearchType[]>>): {
    migrated: Partial<Record<string, SavedSearchType[]>>
    pendingPosts: Array<[string, SavedSearchType[]]>
} {
    const migrated: Partial<Record<string, SavedSearchType[]>> = { ...settings }
    const pendingPosts: Array<[string, SavedSearchType[]]> = []

    for (const scope of SearchScopeSchema.options) {
        if (!supportsColumnViews(scope)) continue
        const colKey = columnScopeKey(scope)
        if (migrated[colKey] !== undefined) continue

        const legacy = parseSearchList(settings[scope])
        if (legacy.length === 0) continue

        const seen = new Set<string>()
        const columnEntries = legacy
            .filter((entry) => entry?.state && hasColumnContent(entry.state))
            .map((entry) => ({
                name: entry.name,
                state: extractColumnState(entry.state),
            }))
            .filter((entry) => {
                if (seen.has(entry.name)) return false
                seen.add(entry.name)
                return true
            })

        migrated[colKey] = columnEntries
        pendingPosts.push([colKey, columnEntries])
    }

    return { migrated, pendingPosts }
}

export const useUserSettingsStore = create<UserSettingsStore>((set, get) => ({
    userSettings: {},

    activeSearches: emptyScopeRecord(),
    savedSearches: emptyScopeRecord(),
    activeColumnViews: emptyScopeRecord(),
    savedColumnViews: emptyScopeRecord(),

    setUserSettings: (settings, options) => {
        let migrated: Partial<Record<string, SavedSearchType[]>> = settings
        const allPosts: Array<[string, SavedSearchType[]]> = []
        try {
            const colSplit = migrateLegacyToColumnViews(settings)
            migrated = colSplit.migrated
            allPosts.push(...colSplit.pendingPosts)
        } catch (err) {
            console.error('[saved-view migration] column-split migration failed', err)
        }
        try {
            const oldMigration = migrateLegacyOldSaves(migrated, {
                hideAgeAndSubGroup: options?.hideAgeAndSubGroup,
            })
            migrated = oldMigration.next
            allPosts.push(...oldMigration.pendingPosts)
        } catch (err) {
            console.error('[saved-view migration] legacy old-saves migration failed', err)
        }
        try {
            const seasonUpgrade = upgradeSeasonFilterShape(migrated)
            migrated = seasonUpgrade.next
            allPosts.push(...seasonUpgrade.pendingPosts)
        } catch (err) {
            console.error('[saved-view migration] season filter shape upgrade failed', err)
        }
        try {
            const bgaFieldUpgrade = upgradeBgaActiveFilterField(migrated)
            migrated = bgaFieldUpgrade.next
            allPosts.push(...bgaFieldUpgrade.pendingPosts)
        } catch (err) {
            console.error('[saved-view migration] bga-active filter field upgrade failed', err)
        }
        try {
            const royaltyRename = renameUnnamedRoyaltyView(migrated)
            migrated = royaltyRename.next
            allPosts.push(...royaltyRename.pendingPosts)
        } catch (err) {
            console.error('[saved-view migration] royalty rename failed', err)
        }
        set({ userSettings: migrated })
        for (const [key, value] of allPosts) {
            gridStatePost({ key, value: JSON.stringify(value) }).catch(() => {})
        }
    },
    getAvailableSearches: (scope: SearchScope | string) =>
        parseSearchList(get().userSettings[scope as string]),
    getAvailableColumnViews: (scope: SearchScope) =>
        parseSearchList(get().userSettings[columnScopeKey(scope)]),

    setActiveSearch: (scope, search) =>
        set((state) => ({
            activeSearches: {
                ...state.activeSearches,
                [scope]: search,
            },
        })),

    setSavedSearch: (scope, search) =>
        set((state) => ({
            savedSearches: {
                ...state.savedSearches,
                [scope]: search,
            },
        })),

    setActiveColumnView: (scope, view) =>
        set((state) => ({
            activeColumnViews: {
                ...state.activeColumnViews,
                [scope]: view,
            },
        })),

    setSavedColumnView: (scope, view) =>
        set((state) => ({
            savedColumnViews: {
                ...state.savedColumnViews,
                [scope]: view,
            },
        })),
}))
